import { useMutation, useQuery } from "react-query";
import {
  accelerateForGrant,
  addBulkResignationExcel,
  addEditPoolSize,
  addGrant,
  addGrantExcel,
  addPlan,
  addTaxExcel,
  bulkExtendExerciseTemplate,
  bulkGenerateGrantLetters,
  bulkResignationConfirm,
  bulkResignationExcelValidation,
  changeOptionStates,
  changePlanState,
  confirmBulkExtendExercise,
  createDigioSignRequest,
  createTrust,
  deleteGrant,
  deletePlan,
  deleteTrust,
  downloadTemplate,
  editGrant,
  editPlan,
  editTrust,
  getAllExercise,
  getAllGrantActivity,
  getAllGrants,
  getAllPlanDocs,
  getAllVestings,
  getBulkStateChangeActionability,
  getCompanyHRMSConfgs,
  getConfigDetails,
  getCreatePlanDetails,
  getEditGrantDetails,
  getEsopAccounting,
  getEsopPlansSummary,
  getPlanGlossary,
  getGrantDetailsForValidation,
  getHistoricReports,
  getOwnersOfPlan,
  getPlanDetails,
  getPlanTemplates,
  getDocumentTemplateVariables,
  getPoolDetails,
  getTransactionsCard,
  getTrustDetails,
  getUserContext,
  getVestingOverrides,
  importAll,
  parseBulkExtendExerciseUpload,
  reverseForfeiture,
  savePlanOwner,
  scheduleHRMSSync,
  signAndApproveGrantLetter,
  updatePlanDocs,
  updateSyncSettings,
  validateBulkExtendExerciseRows,
  validateOnboardingExcel,
  getChangeRequests,
  approveChangeRequest,
  rejectChangeRequest,
  internalTransfer,
  getPoolTransactions,
  createPoolTransaction,
  updatePoolTransaction,
  deletePoolTransaction,
} from "../api/Esop";
import { exportExcel, exportExcelManager } from "../api/ExportExcel";
import { GenericAxiosResponse } from "../types/EsopPlan";
import { PlanExercise } from "../types/Exercise";
import {
  EmployeeDetails,
  Grant,
  GrantResponse,
  PlanDetails,
  ValidationObj,
} from "../types/Grant";
import { PlanVesting } from "../types/Vest";
import { queryClient } from "./client";
import { GetAllGrantsRequest, PendingRequest } from "./pendingRequests";
import { randomGrant, randomPlan } from "./placeholders";
import { updateExportConfig } from "../api/employeeImport";

interface StructuredGrantResponse {
  grants: Grant[];
  plans: PlanDetails;
  employees: EmployeeDetails;
  page?: number;
  totalPages?: number;
  pageSize?: number;
}
export function useGetPlanGlossary() {
  return useQuery({
    queryKey: "planGlossary",
    queryFn: async () => {
      const data = await getPlanGlossary();
      return data.data;
    },
    staleTime: Infinity,
  });
}

export function useGetDocumentTemplateVariables(templateType: string) {
  return useQuery({
    queryKey: ["documentTemplateVariables", templateType],
    queryFn: async () => {
      const data = await getDocumentTemplateVariables(templateType);
      return data.data;
    },
    staleTime: Infinity,
    enabled: !!templateType,
  });
}

export function useEsopPlans(enabled: boolean = true) {
  return useQuery({
    queryKey: "esopPlansSummary",
    queryFn: async () => {
      const data = await getEsopPlansSummary();
      return data.data;
    },
    placeholderData: Array(10)
      .fill(0)
      .map((_i) => randomPlan()),
    enabled,
  });
}
export function usePoolDetails() {
  return useQuery({
    queryKey: "getPoolDetails",
    queryFn: async () => {
      const data = await getPoolDetails();
      return data.data;
    },
  });
}

export function useTrustDetails() {
  return useQuery({
    queryKey: "getTrustDetails",
    queryFn: async () => {
      const data = await getTrustDetails();
      return data.data;
    },
  });
}

export function useCreateTrust() {
  return useMutation({
    mutationKey: "addTrust",
    mutationFn: createTrust,
    onSuccess: () => {
      queryClient.invalidateQueries("getTrustDetails");
      queryClient.refetchQueries("getTrustDetails");
      queryClient.invalidateQueries("createPlanDetails");
      queryClient.refetchQueries("createPlanDetails");
    },
  });
}

export function useEditTrust() {
  return useMutation({
    mutationKey: "editTrust",
    mutationFn: editTrust,
    onSuccess: () => {
      queryClient.invalidateQueries("getTrustDetails");
      queryClient.refetchQueries("getTrustDetails");
      queryClient.invalidateQueries("createPlanDetails");
      queryClient.refetchQueries("createPlanDetails");
    },
  });
}

export function useDeleteTrust() {
  return useMutation({
    mutationKey: "deleteTrust",
    mutationFn: deleteTrust,
    onSuccess: () => {
      queryClient.invalidateQueries("getTrustDetails");
      queryClient.refetchQueries("getTrustDetails");
      queryClient.invalidateQueries("createPlanDetails");
      queryClient.refetchQueries("createPlanDetails");
    },
  });
}

export function usePoolTransactions() {
  return useQuery({
    queryKey: "getPoolTransactions",
    queryFn: async () => {
      const data = await getPoolTransactions();
      return data.data;
    },
  });
}

export function useCreatePoolTransaction() {
  return useMutation({
    mutationKey: "createPoolTransaction",
    mutationFn: createPoolTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries("getPoolTransactions");
      queryClient.refetchQueries("getPoolTransactions");
    },
  });
}

export function useUpdatePoolTransaction() {
  return useMutation({
    mutationKey: "updatePoolTransaction",
    mutationFn: updatePoolTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries("getPoolTransactions");
      queryClient.refetchQueries("getPoolTransactions");
    },
  });
}

export function useDeletePoolTransaction() {
  return useMutation({
    mutationKey: "deletePoolTransaction",
    mutationFn: deletePoolTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries("getPoolTransactions");
      queryClient.refetchQueries("getPoolTransactions");
    },
  });
}

export function useAddPlan() {
  return useMutation({
    mutationKey: "addPlan",
    mutationFn: addPlan,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrants");
    },
    onError: () => {},
  });
}

export function useGrants(params?: GetAllGrantsRequest) {
  return useQuery<
    StructuredGrantResponse,
    Error,
    { grants: Grant[]; page?: number; totalPages?: number; pageSize?: number }
  >({
    queryKey: ["allGrants", params],
    staleTime: 9999,
    cacheTime: 9999,
    queryFn: async () => {
      const resultantQueryParams: GetAllGrantsRequest = {};
      if (params) {
        if (params.planId) resultantQueryParams.planId = params.planId;
        if (params.grantIds) resultantQueryParams.grantIds = params.grantIds;
        if (params.employeeId)
          resultantQueryParams.employeeId = params.employeeId;
        if (params.page) resultantQueryParams.page = params.page;
        if (params.pageSize) resultantQueryParams.pageSize = params.pageSize;
      }

      // Fetch first page (or the requested page)
      const firstResp = await getAllGrants(resultantQueryParams);
      const first = firstResp?.data ?? firstResp ?? {};
      const firstGrants: Grant[] = Array.isArray(first.grants)
        ? first.grants
        : [];
      const totalPages: number =
        typeof first.totalPages === "number" ? first.totalPages : 1;
      const currentPage: number =
        typeof first.page === "number"
          ? first.page
          : resultantQueryParams.page ?? 1;

      // If a specific page was requested, return only that page
      if (resultantQueryParams.page && totalPages >= currentPage) {
        return {
          grants: firstGrants,
          plans: first.plans || {},
          employees: first.employees || {},
          page: currentPage,
          totalPages,
          pageSize: first.pageSize,
        } as StructuredGrantResponse;
      }

      // Otherwise aggregate all pages
      const grantsAcc: Grant[] = [...firstGrants];
      const employeesAcc = { ...(first.employees || {}) };
      const plansAcc = { ...(first.plans || {}) };

      if (totalPages > currentPage) {
        const fetches: Promise<any>[] = [];
        for (let p = currentPage + 1; p <= totalPages; p++) {
          fetches.push(getAllGrants({ ...resultantQueryParams, page: p }));
        }

        try {
          const results = await Promise.all(fetches);
          for (const r of results) {
            const d = r?.data ?? r ?? {};
            if (Array.isArray(d.grants)) grantsAcc.push(...d.grants);
            if (d.employees && typeof d.employees === "object")
              Object.assign(employeesAcc, d.employees);
            if (d.plans && typeof d.plans === "object")
              Object.assign(plansAcc, d.plans);
          }
        } catch {
          // ignore page fetch failures — we still return what we have
        }
      }

      return {
        grants: grantsAcc,
        employees: employeesAcc,
        plans: plansAcc,
        page: currentPage,
        totalPages,
        pageSize: first.pageSize,
      } as StructuredGrantResponse;
    },
    select: ({ grants, employees, plans, page, totalPages, pageSize }) => {
      grants = grants?.sort(
        (a, b) =>
          new Date(a.grantDate).getTime() - new Date(b.grantDate).getTime()
      );
      let cumulativeGrantedOptions = 0;
      const grantsWithCumulation: Grant[] = [];

      for (const grant of grants) {
        cumulativeGrantedOptions += grant.optionsGranted;

        grant.employeeIdentifier = employees[grant.employeeId]?.hrId;
        grant.employeeName = employees[grant.employeeId]?.name;
        grant.planName = plans[grant.planid]?.name;
        grant.planType = plans[grant.planid]?.type;
        grant.isFractional = plans[grant.planid]?.isFractional;
        grant.optionHolderName = employees[grant.employeeId]?.name;
        grant.employmentStatus = employees[grant.employeeId]?.status;

        const vestingExtended = {
          ...grant,
          cumulativeGrantedOptions,
        };

        grantsWithCumulation.push(vestingExtended);
      }

      return { grants: grantsWithCumulation, page, totalPages, pageSize };
    },
    placeholderData: {
      grants: Array(60)
        .fill({})
        .map((_i) => randomGrant()),
      plans: {},
      employees: {},
    },
  });
}

export function useGetGrantDetailsForValidation(params: {
  identifier?: boolean;
  dateOfGrant?: boolean;
}) {
  return useQuery({
    queryKey: ["getGrantDetailsForValidation", params],
    queryFn: () => getGrantDetailsForValidation(params),
    enabled:
      params.identifier !== undefined || params.dateOfGrant !== undefined,
    staleTime: 3600000,
  });
}

export function useGrant(id: string) {
  const { data: _grants, ...rest } = useGrants({ grantIds: [id] });
  const { grants } = _grants || {};
  return {
    grant: grants ? grants[0] : undefined,
    ...rest,
  };
}

export function useAllVestings() {
  return [randomGrant()];
}

export function useAllExercises() {
  const grants = [{ ...randomGrant(), exercises: [], vestings: [] }];
  const exercises = grants.flatMap((g) => g.exercises);
  const cumulativeExercisedOptions = 0;
  const exerciseWithCumulation: PlanExercise[] = [];
  return exerciseWithCumulation;
}

export function useAddGrant() {
  return useMutation({
    mutationKey: "addGrant",
    mutationFn: addGrant,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("getEditGrantDetails");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("vestingEvents");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("vestingEvents");
    },
    onError: () => {},
  });
}

export function useGetEditGrantDetails(id: string) {
  return useQuery({
    queryKey: ["getEditGrantDetails", id],
    queryFn: getEditGrantDetails,
    enabled: id !== "",
    staleTime: 3600000,
  });
}

export function useEditGrant() {
  return useMutation({
    mutationKey: "editGrant",
    mutationFn: editGrant,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("getEditGrantDetails");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("vestingEvents");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("vestingEvents");
    },
    onError: () => {},
  });
}

export function useTransactionsCard() {
  return useQuery({
    queryKey: "transactionsCard",
    queryFn: async () => {
      const response = await getTransactionsCard();
      return response.data;
    },
  });
}

export function useAddGrantExcel() {
  return useMutation({
    mutationKey: "addGrantExcel",
    mutationFn: addGrantExcel,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("vestingEvents");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("vestingEvents");
    },
    onError: (error: { response: { data: { data: GrantResponse } } }) => {},
  });
}

export function useAddBulkResignationExcel() {
  return useMutation({
    mutationKey: "addBulkResignationExcel",
    mutationFn: addBulkResignationExcel,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("vestingEvents");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("vestingEvents");
      return data;
    },
    onError: (error: { response: { data: { errorMessage: string } } }) => {},
  });
}

export function useBulkResignationExcelValidation() {
  return useMutation({
    mutationKey: "bulkResignationExcelValidation",
    mutationFn: bulkResignationExcelValidation,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("vestingEvents");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("vestingEvents");
      return data;
    },
    onError: (error: { response: { data: { errorMessage: string } } }) => {},
  });
}

export function useBulkResignationConfirm() {
  return useMutation({
    mutationKey: "bulkResignationConfirm",
    mutationFn: bulkResignationConfirm,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("vestingEvents");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("vestingEvents");
      return data;
    },
    onError: (error: { response: { data: { errorMessage: string } } }) => {},
  });
}
export function useAddTaxExcel() {
  return useMutation({
    mutationKey: "addGrantExcel",
    mutationFn: addTaxExcel,
    onSuccess: () => {},
    onError: (error: {
      response: { data: { errors: ValidationObj[][] | string } };
    }) => {},
  });
}

export function useGetEsopPlanDetails(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["getPlanDetails", id],
    queryFn: async () => (await getPlanDetails(id)).data,
    onSuccess: () => {
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.refetchQueries("esopPlansSummary");
    },
    enabled,
  });
}

export function useChangeOptionStates() {
  return useMutation({
    mutationKey: "changeOptionState",
    mutationFn: changeOptionStates,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("employeeVestingTableDetails");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.invalidateQueries("employeePageDetails");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("employeeVestingTableDetails");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("employeePageDetails");
    },
    onError: () => {},
  });
}

export function useBulkStateChangeActionability(
  grantIds: string[],
  options: { enabled?: boolean } = {}
) {
  return useQuery(
    ["bulkStateChangeActionability", grantIds],
    () => getBulkStateChangeActionability(grantIds),
    {
      enabled: grantIds.length > 0 && options.enabled !== false,
      staleTime: 0,
    }
  );
}
export function useEditPlan() {
  return useMutation({
    mutationKey: "editPlan",
    mutationFn: editPlan,
    onSuccess: (data) => {
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("createPlanDetails");
      queryClient.refetchQueries("createPlanDetails");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.invalidateQueries("documents");
      queryClient.refetchQueries("documents");
    },
  });
}

export function usePlanStateChange() {
  return useMutation({
    mutationKey: "changePlanState",
    mutationFn: changePlanState,
    onSuccess: () => {
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.invalidateQueries("createPlanDetails");
      queryClient.refetchQueries("createPlanDetails");
      queryClient.invalidateQueries("documents");
      queryClient.refetchQueries("documents");
    },
    onError: () => {},
  });
}

export function useAllGrantActivity() {
  return useQuery({
    queryKey: "allGrantActivity",
    queryFn: async () => {
      const data = await getAllGrantActivity();
      return data.data;
    },
  });
}

export function useRecentDrafts() {
  const { data: grantActivities, ...rest } = useAllGrantActivity();
  const createdStateGrants: PendingRequest[] = (grantActivities || [])
    .filter((grantActivity) => grantActivity.optionState === "CREATED")
    .map((grantActivity) => ({
      id: grantActivity.optionHolderId,
      type: "Grant Created",
      subject: `${grantActivity.optionHolderName} - New Grant`,
      requestDate: new Date(grantActivity.dateOfTransaction),
    }));
  createdStateGrants.sort(
    (a, b) =>
      new Date(b.requestDate || new Date()).getTime() -
      new Date(a.requestDate || new Date()).getTime()
  );
  return {
    data: createdStateGrants,
    ...rest,
  };
}

export function useRecentGrantOffered() {
  const { data: grantActivities, ...rest } = useAllGrantActivity();
  const offeredStateGrants: PendingRequest[] = (grantActivities || [])
    .filter((grantActivity) => grantActivity.optionState === "OFFERED")
    .map((grantActivity) => ({
      id: grantActivity.optionHolderId,
      type: "Grant Offered",
      subject: `${grantActivity.optionHolderName} - Yet to accept offer`,
      requestDate: new Date(grantActivity.dateOfTransaction),
    }));
  offeredStateGrants.sort(
    (a, b) =>
      new Date(b.requestDate || new Date()).getTime() -
      new Date(a.requestDate || new Date()).getTime()
  );
  return {
    data: offeredStateGrants,
    ...rest,
  };
}

export function useRecentForApprovals() {
  const { data: grantActivities, ...rest } = useAllGrantActivity();
  const forApprovalStateGrants: PendingRequest[] = (grantActivities || [])
    .filter((grantActivity) => grantActivity.optionState === "FOR_APPROVAL")
    .map((grantActivity) => ({
      id: grantActivity.optionHolderId,
      type: "Grant For Approval",
      subject: `${grantActivity.optionHolderName} - For Approval`,
      requestDate: new Date(grantActivity.dateOfTransaction),
    }));
  forApprovalStateGrants.sort(
    (a, b) =>
      new Date(b.requestDate || new Date()).getTime() -
      new Date(a.requestDate || new Date()).getTime()
  );
  return {
    data: forApprovalStateGrants,
    ...rest,
  };
}

export function useDeleteGrant() {
  return useMutation({
    mutationKey: "deleteGrant",
    mutationFn: deleteGrant,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.invalidateQueries("allExercises");
      queryClient.invalidateQueries("allVestings");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("allExercises");
      queryClient.refetchQueries("allVestings");
    },
    onError: () => {},
  });
}

export function useReverseForfeiture() {
  return useMutation({
    mutationKey: "reverseForfeiture",
    mutationFn: reverseForfeiture,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("allGrants");
    },
  });
}

export function useBulkExtendExerciseTemplate() {
  return useMutation({
    mutationKey: "bulkExtendExerciseTemplate",
    mutationFn: bulkExtendExerciseTemplate,
  });
}

export function useParseBulkExtendExerciseUpload() {
  return useMutation({
    mutationKey: "parseBulkExtendExerciseUpload",
    mutationFn: parseBulkExtendExerciseUpload,
  });
}

export function useValidateBulkExtendExerciseRows() {
  return useMutation({
    mutationKey: "validateBulkExtendExerciseRows",
    mutationFn: validateBulkExtendExerciseRows,
  });
}

export function useConfirmBulkExtendExercise() {
  return useMutation({
    mutationKey: "confirmBulkExtendExercise",
    mutationFn: confirmBulkExtendExercise,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("allGrants");
    },
  });
}

export function useDeletePlan() {
  return useMutation({
    mutationKey: "deletePlan",
    mutationFn: deletePlan,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("employeesOverview");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("employeesOverview");
    },
    onError: (error: GenericAxiosResponse) => {},
  });
}

export function useCreatePlanDetails() {
  return useQuery({
    queryKey: "createPlanDetails",
    queryFn: async () => {
      const data = await getCreatePlanDetails();
      return data.data;
    },
  });
}

export function useOwnersOfPlan(planId: string) {
  return useQuery({
    queryKey: ["getOwnersOfPlan", planId],
    queryFn: getOwnersOfPlan,
  });
}

export function useEditAddPoolSize() {
  return useMutation({
    mutationKey: "AddEditPoolSize",
    mutationFn: addEditPoolSize,
    onSuccess: () => {
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.invalidateQueries("getPoolDetails");
      queryClient.refetchQueries("getPoolDetails");
    },
    onError: () => {},
  });
}

export function useExportToExcel() {
  return useMutation({
    mutationKey: "excelExport",
    mutationFn: exportExcel,
  });
}

export function useExportToExcelManager() {
  return useMutation({
    mutationKey: "excelExportManager",
    mutationFn: exportExcelManager,
  });
}

export function useDownloadEsopAccounting() {
  return useMutation({
    mutationKey: "downloadEsopAccounting",
    mutationFn: getEsopAccounting,
  });
}

export function useOwnerShip() {
  return useMutation({
    mutationKey: "ownerShip",
    mutationFn: savePlanOwner,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("esopPlansSummary");
      queryClient.invalidateQueries("allGrantActivity");
      queryClient.invalidateQueries("getOwnersOfPlan");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("esopPlansSummary");
      queryClient.refetchQueries("allGrantActivity");
      queryClient.refetchQueries("getOwnersOfPlan");
    },
  });
}

export function useValidateExcel() {
  return useMutation({
    mutationKey: "validateOnboardingExcel",
    mutationFn: validateOnboardingExcel,
  });
}

export function useDownloadTemplate() {
  return useQuery({
    queryKey: "downloadTemplate",
    queryFn: downloadTemplate,
    enabled: false,
    select: (data) => data,
  });
}

export function useImportExcel() {
  return useMutation({
    mutationKey: "importAll",
    mutationFn: importAll,
  });
}

export function useGenerateGrantLettersBulk() {
  return useMutation({
    mutationKey: "bulkGenerateGrantLetters",
    mutationFn: bulkGenerateGrantLetters,
    onSuccess: () => {
      queryClient.invalidateQueries("documents");
      queryClient.refetchQueries("documents");
    },
  });
}

export function useGetAllPlanDocs(planId: string) {
  return useQuery({
    queryKey: ["getAllPlanDocs", planId],
    queryFn: async () => (await getAllPlanDocs(planId)).data,
    staleTime: 9999,
    cacheTime: 9999,
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
}

export function useUpdatePlanDocs() {
  return useMutation({
    mutationKey: ["updatePlan"],
    mutationFn: updatePlanDocs,
    onSuccess: () => {
      queryClient.invalidateQueries("getAllPlanDocs", { exact: false });
      queryClient.refetchQueries("getAllPlanDocs", { exact: false });
      queryClient.invalidateQueries("documents");
      queryClient.refetchQueries("documents");
    },
  });
}

export function useSignAndApproveGrant() {
  return useMutation({
    mutationKey: ["signGrant"],
    mutationFn: signAndApproveGrantLetter,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.invalidateQueries("transactionsCard");
      queryClient.refetchQueries("allGrants");
      queryClient.refetchQueries("transactionsCard");
    },
  });
}
export function useCreateESignRequest() {
  return useMutation({
    mutationFn: createDigioSignRequest,
    mutationKey: "createDigioSignRequest",
  });
}

export function useAccelerateForGrant() {
  return useMutation({
    mutationKey: "accelerateForGrant",
    mutationFn: accelerateForGrant,
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("allGrants");
      queryClient.invalidateQueries("employeeVestingTableDetails");
      queryClient.refetchQueries("employeeVestingTableDetails");
    },
  });
}

export function useGetVestingOverrides() {
  return useQuery({
    queryKey: "vestingOverrides",
    queryFn: getVestingOverrides,
  });
}

export function usePlanTemplates() {
  return useQuery({
    queryKey: "planTemplates",
    queryFn: async () => {
      const response = await getPlanTemplates();
      return response.data;
    },
  });
}

export function useGetAllCompanyConfigs(excludeHistory = false) {
  return useQuery({
    queryKey: ["companyConfigs", excludeHistory],
    queryFn: getCompanyHRMSConfgs,
  });
}

export function useGetConfigDetails(configId: string) {
  return useQuery({
    queryKey: ["syncDetails", configId],
    queryFn: getConfigDetails,
    enabled: configId !== "",
  });
}

export function useScheduleSync() {
  return useMutation({
    mutationKey: "scheduleHRMSSync",
    mutationFn: scheduleHRMSSync,
    onSuccess: () => {
      queryClient.invalidateQueries("companyConfigs");
      queryClient.refetchQueries("companyConfigs");
    },
  });
}

export function useUpdateSyncSettings() {
  return useMutation({
    mutationKey: "updateSyncSettings",
    mutationFn: updateSyncSettings,
    onSuccess: () => {
      queryClient.invalidateQueries("syncDetails");
      queryClient.invalidateQueries("companyConfigs");
      queryClient.refetchQueries("companyConfigs");
      queryClient.refetchQueries("syncDetails");
    },
  });
}

export function useGetUserContext() {
  return useQuery({
    queryKey: ["getUserContext"],
    queryFn: getUserContext,
    enabled: false,
  });
}

export function useUpdateExportSettings() {
  return useMutation({
    mutationKey: "updateMasterSettings",
    mutationFn: updateExportConfig,
    onSuccess: () => {
      queryClient.invalidateQueries("syncDetails");
      queryClient.invalidateQueries("companyConfigs");
      queryClient.refetchQueries("companyConfigs");
      queryClient.refetchQueries("syncDetails");
    },
  });
}

export function useGetHistoricReports() {
  return useQuery({
    queryKey: "getHistoricReports",
    queryFn: getHistoricReports,
  });
}

export function useGetChangeRequests() {
  return useQuery({
    queryKey: "changeRequests",
    queryFn: async () => {
      const response = await getChangeRequests();
      return response;
    },
  });
}

export function useApproveChangeRequest() {
  return useMutation({
    mutationKey: "approveChangeRequest",
    mutationFn: approveChangeRequest,
    onSuccess: () => {
      queryClient.invalidateQueries("changeRequests");
    },
  });
}

export function useRejectChangeRequest() {
  return useMutation({
    mutationKey: "rejectChangeRequest",
    mutationFn: rejectChangeRequest,
    onSuccess: () => {
      queryClient.invalidateQueries("changeRequests");
    },
  });
}

export function useInternalTransfer() {
  return useMutation({
    mutationKey: "internalTransfer",
    mutationFn: internalTransfer,
    onSuccess: () => {
      queryClient.invalidateQueries("employees");
    },
  });
}
